import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Leaf, Heart, Sparkles } from "lucide-react";

export default function About() {
  return (
    <main data-testid="about-page" className="pt-28">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-[#faf6ef]">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center">
          <div className="text-[#b4462a] text-xs tracking-[0.3em] uppercase font-bold mb-4">About</div>
          <h1 className="text-5xl md:text-7xl leading-[1.05] tracking-tight font-light">A quiet house of craft, built to scale.</h1>
          <p className="mt-6 text-lg text-[#1a1a1a]/70 max-w-2xl mx-auto leading-[1.8]">Founded in Jaipur in 2007, Indus Art is a third-generation block-print workshop that grew into a wholesale export house without losing its hand.</p>
        </div>
      </section>

      {/* Workshop story */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14 grid md:grid-cols-12 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="md:col-span-6">
            <div className="aspect-[4/5] overflow-hidden rounded-[2.5rem] shadow-soft">
              <img src="https://images.unsplash.com/photo-1768746350424-ee28a364dcf5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwzfHx0ZXh0aWxlJTIwZmFjdG9yeSUyMHdvcmtzaG9wfGVufDB8fHx8MTc3NjU3NDYwMXww&ixlib=rb-4.1.0&q=85" alt="Jaipur unit" className="w-full h-full object-cover" />
            </div>
          </motion.div>
          <div className="md:col-span-6">
            <div className="text-[#8a9a7b] text-xs tracking-[0.3em] uppercase font-bold mb-4">The Workshop</div>
            <h2 className="text-4xl md:text-5xl leading-[1.1] font-light mb-6">Single-roof manufacturing, Jaipur.</h2>
            <p className="text-[#1a1a1a]/80 leading-[1.8] text-lg mb-5">Fabric arrives from vetted Indian mills. It is washed, printed, dried, stitched, finished, inspected, and packed — all inside one facility, by one team, under one schedule.</p>
            <p className="text-[#1a1a1a]/75 leading-[1.8]">No trading companies between you and the press. No hidden sub-contractors. If something goes sideways, there is one phone number.</p>
          </div>
        </div>
      </section>

      {/* Stats band */}
      <section className="py-20 bg-[#1a1a1a] text-[#faf6ef]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14 grid md:grid-cols-4 gap-10">
          {[
            { n: "2007", l: "Founded in Jaipur" },
            { n: "30+",  l: "Export destinations" },
            { n: "100+", l: "Skilled artisans & craftsmen" },
            { n: "100%", l: "Inspection coverage" },
          ].map((x, i) => (
            <div key={i} data-testid={`about-stat-${i}`}>
              <div className="text-5xl text-[#d98f2f] font-extrabold leading-none">{x.n}</div>
              <div className="text-xs tracking-[0.25em] uppercase text-[#faf6ef]/70 mt-3 font-bold">{x.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section data-testid="philosophy-section" className="py-24 md:py-32 bg-[#8a9a7b] text-[#faf6ef] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
             style={{ backgroundImage: "radial-gradient(circle at 30% 20%, #faf6ef 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
        <div className="relative max-w-[1100px] mx-auto px-6 md:px-10 text-center">
          <div className="text-[#faf6ef]/70 text-xs tracking-[0.3em] uppercase font-bold mb-6">Our Beliefs</div>
          <h2 className="text-5xl md:text-7xl leading-[1.05] tracking-tight font-light mb-12">Our Philosophy</h2>

          <div className="space-y-8 text-base md:text-lg leading-[1.9] text-[#faf6ef]/95 max-w-3xl mx-auto">
            <p>
              In addition to our focus on artistic excellence, we prioritize sustainability and ethical practices throughout our manufacturing process. We source our materials responsibly, supporting local communities and promoting eco-friendly initiatives. By choosing our products, customers can indulge in beautiful home decor while contributing to a more sustainable future.
            </p>
            <p>
              As we continue to evolve and expand, we remain dedicated to our core values of authenticity, quality, and customer satisfaction. We invite you to experience the timeless beauty of our block print creations and join us in celebrating the rich cultural heritage of Jaipur.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-4 mt-14 max-w-3xl mx-auto">
            {[
              { Icon: Heart,     t: "Authenticity",  d: "Genuine, hand-pressed craft. No shortcuts." },
              { Icon: Leaf,      t: "Sustainability", d: "Responsible sourcing, eco-friendly chemistry." },
              { Icon: Sparkles,  t: "Quality",       d: "100% inspection, batch after batch." },
            ].map(({ Icon, t, d }, i) => (
              <div key={t} data-testid={`philosophy-pillar-${i}`} className="rounded-3xl glass-dark p-6 text-left">
                <Icon size={22} strokeWidth={1.8} className="text-[#d98f2f] mb-3" />
                <div className="font-bold text-lg mb-1.5">{t}</div>
                <div className="text-sm text-[#faf6ef]/75 leading-[1.6]">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OUR TEAM */}
      <section data-testid="team-section" className="py-24 md:py-32 bg-[#faf6ef]">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center mb-12 md:mb-16">
          <div className="text-[#b4462a] text-xs tracking-[0.3em] uppercase font-bold mb-4">The People</div>
          <h2 className="text-5xl md:text-7xl leading-[1.05] tracking-tight font-light">Our Team</h2>
        </div>

        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="rounded-[2rem] overflow-hidden shadow-soft">
            <img src="/team-strip.png" alt="The Indus Art team — artisans, craftsmen, and quality team in our Jaipur workshop" className="w-full h-auto block" />
          </div>
        </div>

        <div className="max-w-[1100px] mx-auto px-6 md:px-10 mt-12 md:mt-16 space-y-8 text-base md:text-lg leading-[1.9] text-[#1a1a1a]/85">
          <p>
            Our commitment to excellence and craftsmanship has enabled us to establish a strong presence in the global market. We currently serve customers in more than 30 countries, exporting our products to discerning individuals, boutique stores, and luxury hotels. Our dedication to maintaining the highest quality standards has garnered us a loyal customer base that appreciates the authenticity and charm of our offerings.
          </p>
          <p>
            Behind our success is a team of skilled artisans and craftsmen, numbering over 100, who bring our designs to life with their expertise and passion. We believe in empowering our employees and fostering a collaborative work environment where creativity thrives. Our team is committed to upholding the traditional artistry of Jaipur and ensuring that each product reflects the skillful craftsmanship that the city is renowned for.
          </p>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-24 bg-white">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <div className="text-[#b4462a] text-xs tracking-[0.3em] uppercase font-bold mb-4">Certifications &amp; Values</div>
          <h2 className="text-4xl md:text-5xl leading-[1.1] font-light mb-10">We work towards the standards our buyers demand.</h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-5 gap-4">
            {["GOTS", "OEKO-TEX", "SA8000", "Sedex", "BSCI"].map((c) => (
              <div key={c} className="border border-[#1a1a1a]/15 rounded-3xl aspect-[3/2] flex items-center justify-center text-xl font-extrabold text-[#1e2a5a]" data-testid={`cert-${c}`}>{c}</div>
            ))}
          </div>
          <p className="text-xs text-[#1a1a1a]/50 mt-4">Placeholder certification logos — replace with verified documents per facility.</p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-[#faf6ef] text-center">
        <div className="max-w-[900px] mx-auto px-6">
          <h2 className="text-4xl md:text-5xl leading-[1.1] font-light mb-6">A quiet house, ready to speak about your project.</h2>
          <Link to="/contact#lead-form" className="btn-pill bg-[#1e2a5a] text-[#faf6ef] hover:bg-[#b4462a] shadow-cta py-4 px-8 text-sm" data-testid="about-cta">
            Talk to a specialist <ArrowRight size={16} strokeWidth={2.2} />
          </Link>
        </div>
      </section>
    </main>
  );
}
